import { CalendarClock, Paperclip, RotateCcw, Save } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  BOARD_SNAPSHOT_RETENTION_DAYS_DEFAULT,
  boardPath,
  type BoardSnapshot,
  type BoardSnapshotList,
  type BoardSnapshotRestoreResponse,
} from '@molt/shared';
import {
  Button,
  Dialog,
  Menu,
  Notice,
  TextField,
  parsePostgresTimestamp,
  type MenuItem,
} from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardSnapshotSectionProps {
  readonly boardTitle: string;
  readonly snapshotList: BoardSnapshotList | undefined;
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly canManage: boolean;
  readonly disabledReason: string | null;
  readonly enabledBusy?: boolean | undefined;
  readonly retentionBusy?: boolean | undefined;
  readonly captureAttachmentsBusy?: boolean | undefined;
  readonly restoreBusySnapshotId?: string | null | undefined;
  readonly enabledError?: string | undefined;
  readonly retentionError?: string | undefined;
  readonly captureAttachmentsError?: string | undefined;
  readonly restoreError?: string | undefined;
  readonly restoreResult?: BoardSnapshotRestoreResponse | null | undefined;
  readonly onSetEnabled: (enabled: boolean) => void;
  readonly onSetRetentionDays: (retentionDays: number | null) => void;
  readonly onSetCaptureAttachments: (captureAttachments: boolean) => void;
  readonly onRestore: (snapshotId: string, title?: string) => void;
  readonly onClearRestoreResult?: (() => void) | undefined;
}

export function BoardSnapshotSection({
  boardTitle,
  snapshotList,
  loading = false,
  loadError,
  canManage,
  disabledReason,
  enabledBusy,
  retentionBusy,
  captureAttachmentsBusy,
  restoreBusySnapshotId,
  enabledError,
  retentionError,
  captureAttachmentsError,
  restoreError,
  restoreResult,
  onSetEnabled,
  onSetRetentionDays,
  onSetCaptureAttachments,
  onRestore,
  onClearRestoreResult,
}: BoardSnapshotSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [retentionDraft, setRetentionDraft] = useState('');
  const [retentionMode, setRetentionMode] = useState<'default' | 'custom'>('custom');
  const [restoreSnapshot, setRestoreSnapshot] = useState<BoardSnapshot | null>(null);
  const [restoreTitle, setRestoreTitle] = useState('');

  useEffect(() => {
    if (snapshotList === undefined) return;
    setRetentionDraft(String(snapshotList.retentionDays));
  }, [snapshotList?.retentionDays]);

  const snapshots = snapshotList?.snapshots ?? [];
  const effectiveRetentionDays =
    snapshotList?.retentionDays ?? BOARD_SNAPSHOT_RETENTION_DAYS_DEFAULT;
  const checking =
    canManage && (loading || (snapshotList === undefined && loadError === undefined));
  const lockedReason = canManage
    ? null
    : (disabledReason ?? t('integrations.snapshot.reason.adminOnly'));
  const unavailableReason = checking
    ? t('integrations.snapshot.checking')
    : (loadError ??
      (snapshotList === undefined ? t('integrations.snapshot.loadFailed') : null));
  const controlsDisabledReason = lockedReason ?? unavailableReason;
  const controlsDisabled = controlsDisabledReason !== null;
  const enabled = snapshotList?.enabled ?? false;
  const captureAttachments = snapshotList?.captureAttachments ?? false;
  const retentionValue = Number(retentionDraft);
  const retentionInvalid =
    retentionDraft.trim() === '' ||
    !Number.isInteger(retentionValue) ||
    retentionValue <= 0;
  const showRetentionValidation =
    snapshotList !== undefined && !controlsDisabled && retentionMode === 'custom';
  const retentionErrorMessage = retentionInvalid
    ? t('integrations.snapshot.retention.invalid')
    : undefined;
  const retentionErrorProps =
    showRetentionValidation && retentionErrorMessage !== undefined
      ? { error: retentionErrorMessage }
      : {};
  const retentionDisabledReason =
    controlsDisabledReason ??
    (showRetentionValidation && retentionInvalid ? retentionErrorMessage : null);
  const enabledBusyProps = enabledBusy === undefined ? {} : { busy: enabledBusy };
  const retentionBusyProps = retentionBusy === undefined ? {} : { busy: retentionBusy };
  const captureAttachmentsBusyProps =
    captureAttachmentsBusy === undefined ? {} : { busy: captureAttachmentsBusy };
  const restoreBusyProps =
    restoreBusySnapshotId === restoreSnapshot?.id ? { busy: true } : {};
  const restoreResultPath =
    restoreResult === null || restoreResult === undefined
      ? null
      : boardPath(restoreResult.board.shortLink, restoreResult.board.slug);
  const retentionItems = useMemo<MenuItem[]>(
    () => [
      {
        id: 'default',
        label: t('integrations.snapshot.retention.optionDefault', {
          days: BOARD_SNAPSHOT_RETENTION_DAYS_DEFAULT,
        }),
        onSelect: () => undefined,
      },
      {
        id: 'custom',
        label: t('integrations.snapshot.retention.optionCustom'),
        onSelect: () => undefined,
      },
    ],
    [t],
  );

  function openRestoreDialog(snapshot: BoardSnapshot): void {
    setRestoreSnapshot(snapshot);
    setRestoreTitle(
      t('integrations.snapshot.restoreTitleDefault', { title: boardTitle }),
    );
  }

  function confirmRestore(): void {
    if (restoreSnapshot === null || restoreBusySnapshotId === restoreSnapshot.id) return;
    const trimmed = restoreTitle.trim();
    onRestore(
      restoreSnapshot.id,
      trimmed.length === 0
        ? t('integrations.snapshot.restoreTitleDefault', { title: boardTitle })
        : trimmed,
    );
    setRestoreSnapshot(null);
  }

  return (
    <>
      <section aria-labelledby="board-snapshots-heading" className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h3 id="board-snapshots-heading" className="text-sm font-semibold text-ink">
            {t('integrations.snapshot.heading')}
          </h3>
          <p className="text-sm text-ink-muted">
            {t('integrations.snapshot.description')}
          </p>
        </div>

        {lockedReason !== null ? <Notice tone="locked" body={lockedReason} /> : null}
        {checking ? (
          <Notice tone="info" body={t('integrations.snapshot.checking')} />
        ) : null}
        {loadError !== undefined ? <Notice tone="bad" body={loadError} /> : null}

        {snapshotList !== undefined && snapshots.length === 0 && enabled === false ? (
          <Notice
            tone="info"
            title={t('integrations.snapshot.emptyDisabledTitle')}
            body={t('integrations.snapshot.emptyDisabledBody')}
          />
        ) : null}
        {snapshotList !== undefined && snapshots.length === 0 && enabled ? (
          <Notice
            tone="info"
            title={t('integrations.snapshot.emptyEnabledTitle')}
            body={t('integrations.snapshot.emptyEnabledBody')}
          />
        ) : null}

        {restoreResult !== null &&
        restoreResult !== undefined &&
        restoreResultPath !== null ? (
          <Notice
            tone="info"
            title={t('integrations.snapshot.restored.title')}
            body={
              <span>
                <a className="font-medium text-accent underline" href={restoreResultPath}>
                  {t('integrations.snapshot.restored.openLink', {
                    title: restoreResult.board.title,
                  })}
                </a>{' '}
                {t('integrations.snapshot.restored.summary', {
                  lists: t(
                    'integrations.snapshot.restored.lists',
                    { count: restoreResult.listsRestored },
                    restoreResult.listsRestored,
                  ),
                  cards: t(
                    'integrations.snapshot.restored.cards',
                    { count: restoreResult.cardsRestored },
                    restoreResult.cardsRestored,
                  ),
                })}
              </span>
            }
            action={
              onClearRestoreResult ? (
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  onClick={onClearRestoreResult}
                >
                  {t('integrations.snapshot.restored.dismiss')}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        <div className="flex flex-col gap-3 rounded-card bg-surface-raised p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="toggle"
              role="switch"
              aria-checked={enabled}
              aria-label={t('integrations.snapshot.toggleLabel')}
              pressed={enabled}
              disabled={controlsDisabled}
              disabledReason={controlsDisabledReason ?? undefined}
              onClick={() => onSetEnabled(!enabled)}
              iconStart={<CalendarClock aria-hidden="true" className="size-4" />}
              {...enabledBusyProps}
            >
              {enabled
                ? t('integrations.snapshot.toggleOn')
                : t('integrations.snapshot.toggleOff')}
            </Button>
            <Button
              type="button"
              variant="toggle"
              role="switch"
              aria-checked={captureAttachments}
              aria-label={t('integrations.snapshot.attachmentsToggleLabel')}
              pressed={captureAttachments}
              disabled={controlsDisabled}
              disabledReason={controlsDisabledReason ?? undefined}
              onClick={() => onSetCaptureAttachments(!captureAttachments)}
              iconStart={<Paperclip aria-hidden="true" className="size-4" />}
              {...captureAttachmentsBusyProps}
            >
              {captureAttachments
                ? t('integrations.snapshot.attachmentsOn')
                : t('integrations.snapshot.attachmentsOff')}
            </Button>
          </div>

          <p className="text-xs text-ink-muted">
            {t('integrations.snapshot.attachmentsNote')}
          </p>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end gap-2">
              <Menu
                trigger={
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label={t('integrations.snapshot.retention.menuLabel', {
                      mode: t(
                        retentionMode === 'default'
                          ? 'integrations.snapshot.retention.mode.default'
                          : 'integrations.snapshot.retention.mode.custom',
                      ),
                      days: effectiveRetentionDays,
                    })}
                    disabled={controlsDisabled}
                    disabledReason={controlsDisabledReason ?? undefined}
                  >
                    {t(
                      'integrations.snapshot.retention.trigger',
                      { count: effectiveRetentionDays },
                      effectiveRetentionDays,
                    )}
                  </Button>
                }
                sections={[
                  {
                    id: 'snapshot-retention-mode',
                    heading: t('integrations.snapshot.retention.menuHeading'),
                    selection: 'radio',
                    value: retentionMode,
                    onValueChange: (value) => {
                      if (value === 'default') {
                        setRetentionMode('default');
                        setRetentionDraft(String(BOARD_SNAPSHOT_RETENTION_DAYS_DEFAULT));
                        onSetRetentionDays(null);
                        return;
                      }
                      setRetentionMode('custom');
                    },
                    items: retentionItems,
                  },
                ]}
              />
              <TextField
                label={t('integrations.snapshot.retention.daysLabel')}
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={retentionDraft}
                disabled={controlsDisabled || retentionMode === 'default'}
                {...retentionErrorProps}
                hint={t('integrations.snapshot.retention.daysHint')}
                wrapperClassName="min-w-[11rem] flex-1"
                onChange={(event) => {
                  setRetentionMode('custom');
                  setRetentionDraft(event.currentTarget.value);
                }}
              />
              <Button
                type="button"
                variant="primary"
                disabled={retentionDisabledReason !== null || retentionMode === 'default'}
                disabledReason={retentionDisabledReason ?? undefined}
                onClick={() => onSetRetentionDays(retentionValue)}
                iconStart={<Save aria-hidden="true" className="size-4" />}
                {...retentionBusyProps}
              >
                {t('integrations.snapshot.retention.saveAction')}
              </Button>
            </div>
            <p className="text-xs text-ink-muted">
              {t('integrations.snapshot.retention.note')}
            </p>
          </div>
        </div>

        {enabledError !== undefined ? <Notice tone="bad" body={enabledError} /> : null}
        {retentionError !== undefined ? (
          <Notice tone="bad" body={retentionError} />
        ) : null}
        {captureAttachmentsError !== undefined ? (
          <Notice tone="bad" body={captureAttachmentsError} />
        ) : null}
        {restoreError !== undefined ? <Notice tone="bad" body={restoreError} /> : null}

        {snapshots.length > 0 ? (
          <ul
            className="flex flex-col gap-2"
            aria-label={t('integrations.snapshot.listLabel')}
          >
            {snapshots.map((snapshot) => {
              const taken = formatAbsoluteDate(snapshot.takenAt);
              const expires = formatAbsoluteDate(snapshot.expiresAt);
              const restoreDisabledReason =
                controlsDisabledReason ??
                (restoreBusySnapshotId !== null &&
                restoreBusySnapshotId !== undefined &&
                restoreBusySnapshotId !== snapshot.id
                  ? t('integrations.snapshot.restoreInProgress')
                  : null);
              const rowRestoreBusy =
                restoreBusySnapshotId === snapshot.id ? { busy: true } : {};

              return (
                <li
                  key={snapshot.id}
                  className="flex flex-col gap-2 rounded-card bg-surface-raised p-3"
                >
                  <div className="grid gap-1 text-sm sm:grid-cols-2">
                    <p className="text-ink">
                      <span className="font-medium">
                        {t('integrations.snapshot.takenLabel')}
                      </span>{' '}
                      <time dateTime={toIsoDateTime(snapshot.takenAt)}>{taken}</time>
                    </p>
                    <p className="text-ink">
                      <span className="font-medium">
                        {t('integrations.snapshot.expiresLabel')}
                      </span>{' '}
                      <time dateTime={toIsoDateTime(snapshot.expiresAt)}>{expires}</time>
                    </p>
                  </div>
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-label={t('integrations.snapshot.restoreRowLabel', {
                        date: taken,
                      })}
                      disabled={restoreDisabledReason !== null}
                      disabledReason={restoreDisabledReason ?? undefined}
                      onClick={() => openRestoreDialog(snapshot)}
                      iconStart={<RotateCcw aria-hidden="true" className="size-4" />}
                      {...rowRestoreBusy}
                    >
                      {t('integrations.snapshot.restoreAction')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      <Dialog
        open={restoreSnapshot !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreSnapshot(null);
        }}
        size="sm"
        title={t('integrations.snapshot.restoreDialog.title')}
        description={t('integrations.snapshot.restoreDialog.description')}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRestoreSnapshot(null)}
            >
              {t('integrations.snapshot.restoreDialog.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={confirmRestore}
              iconStart={<RotateCcw aria-hidden="true" className="size-4" />}
              {...restoreBusyProps}
            >
              {t('integrations.snapshot.restoreDialog.confirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <TextField
            label={t('integrations.snapshot.restoreDialog.titleLabel')}
            value={restoreTitle}
            onChange={(event) => setRestoreTitle(event.currentTarget.value)}
          />
          <Notice tone="warn" body={t('integrations.snapshot.restoreDialog.warning')} />
        </div>
      </Dialog>
    </>
  );
}

function formatAbsoluteDate(value: string): string {
  const parsed = parsePostgresTimestamp(value);
  if (parsed === null) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(parsed);
}

function toIsoDateTime(value: string): string {
  return parsePostgresTimestamp(value)?.toISOString() ?? value;
}
